// Cave84 — Top bar · Bronze Nocturne
// Brand: CAVE eighty-four (Fraunces italic accent) · nav bg-3 active · search ⌘K · avatar bronze

import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { signOut } from '@/services/auth';
import { NotificationCenter } from './NotificationCenter';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { usePrivacy } from '@/hooks/usePrivacy';

const BN = {
  bg: '#0E1116', bg2: '#141821', bg3: '#1B202B',
  hair: '#232936', hair2: '#2F3645',
  ink: '#ECE6D4', ink3: '#9C9483', ink4: '#908977',
  accent: '#C49255',
};

const MAIN_NAV = [
  { to: '/',                  label: 'Dashboard',     end: true  },
  { to: '/inventory',         label: 'Inventory'                 },
  { to: '/drink-soon',        label: 'Drink Soon'                },
  { to: '/wines',             label: 'Wine Analysis'             },
  { to: '/vintage-charts',    label: 'Vintage Charts'            },
  { to: '/wishlist',          label: 'Wishlist'                  },
  { to: '/consumed',          label: 'Consumed'                  },
  { to: '/special-occasions', label: 'Special'                   },
] as const;

// Owner-only — hides purchase-price / gain-loss data from Guests.
const OWNER_ONLY_NAV = [
  { to: '/prices/investment', label: 'Investment' },
  { to: '/import',            label: 'Import'     },
] as const;

// ---------------------------------------------------------------------------
// Privacy toggle — eye / eye-off. Flips the app-wide currency-hiding switch.
// Starts as eye-off (values hidden), inviting reveal. Placed left of avatar.
// ---------------------------------------------------------------------------
function PrivacyToggle() {
  const { hidden, toggle } = usePrivacy();

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={hidden ? 'Reveal values' : 'Hide values'}
      aria-pressed={!hidden}
      title={hidden ? 'Reveal values' : 'Hide values'}
      style={{
        width: 30, height: 30, borderRadius: 6,
        display: 'grid', placeItems: 'center',
        background: 'transparent', border: 'none',
        color: hidden ? BN.ink3 : BN.accent, cursor: 'pointer', flexShrink: 0,
      }}
    >
      {hidden ? (
        // eye-off
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6 0-10-6-10-6a20.3 20.3 0 0 1 4.22-5.06M9.9 4.24A10.94 10.94 0 0 1 12 4c6 0 10 6 10 6a20.3 20.3 0 0 1-2.16 2.94M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        // eye
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-6 11-6 11 6 11 6-4 6-11 6-11-6-11-6z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );
}

export function TopBar() {
  const userDoc = useAuthStore((s) => s.userDoc);
  const navigate = useNavigate();
  const location = useLocation();
  // SearchPage renders its own dedicated (autofocused, live-filtering) search
  // bar directly under the header. Showing TopBar's quick-search box there
  // too created two disconnected inputs stacked on top of each other.
  const onSearchPage = location.pathname === '/search';
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { canInstall, promptInstall, isInstalling } = useInstallPrompt();

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  const initial = (userDoc?.display_name?.[0] ?? userDoc?.email?.[0] ?? 'M').toUpperCase();

  const navStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '6px 12px', borderRadius: 5,
    color: isActive ? BN.ink : BN.ink3,
    background: isActive ? BN.bg3 : 'transparent',
    textDecoration: 'none', fontSize: 12.5, fontWeight: 450,
    whiteSpace: 'nowrap', transition: 'color 0.15s, background 0.15s',
  });

  const menuBtn: React.CSSProperties = {
    width: '100%', textAlign: 'left', padding: '10px 14px',
    fontSize: 13, color: BN.ink3, background: 'transparent',
    border: 'none', cursor: 'pointer',
  };

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 30, height: 56,
      display: 'flex', alignItems: 'center', gap: 28, padding: '0 32px',
      borderBottom: `1px solid ${BN.hair}`,
      background: 'rgba(14,17,22,0.85)', backdropFilter: 'blur(10px)',
    }}>

      <Link to="/" style={{
        fontFamily: "'Fraunces', serif", fontWeight: 350, fontSize: 19,
        letterSpacing: '0.04em', color: BN.ink, textDecoration: 'none',
        display: 'inline-flex', alignItems: 'baseline', gap: 4, flexShrink: 0,
      }}>
        CAVE <span style={{ fontWeight: 400, fontStyle: 'italic', color: BN.accent }}>84</span>
      </Link>

      <nav className="hidden md:flex" style={{ gap: 4, marginLeft: 12, overflowX: 'clip' }}>
        {MAIN_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={'end' in item ? item.end : undefined}
            style={({ isActive }) => navStyle(isActive)}
          >
            {item.label}
          </NavLink>
        ))}
        {userDoc?.role === 'owner' && OWNER_ONLY_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            style={({ isActive }) => navStyle(isActive)}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>

        {!onSearchPage && (
          <form onSubmit={handleSearch} className="hidden md:flex" role="search">
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', border: `1px solid ${BN.hair2}`,
              borderRadius: 6, background: BN.bg2, fontSize: 12, color: BN.ink3,
              width: 220, flexShrink: 0,
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search" aria-label="Search the cellar"
                style={{ flex: 1, minWidth: 0, background: 'transparent', outline: 'none', border: 'none', color: BN.ink, fontSize: 12 }}
              />
            </div>
          </form>
        )}

        {/* NOTE: `display` MUST stay in the class list, never in `style`. An inline
            `display:grid` beats Tailwind's `.md\:hidden`, which is what made this
            button render beside the desktop search box on wide screens. */}
        {!onSearchPage && (
          <button type="button" onClick={() => navigate('/search')} aria-label="Search"
            className="md:hidden grid place-items-center"
            style={{ width: 30, height: 30, borderRadius: 6, background: 'transparent', border: 'none', color: BN.ink3, cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
            </svg>
          </button>
        )}

        {canInstall && (
          <button type="button" onClick={() => void promptInstall()} disabled={isInstalling}
            aria-label="Install Cave84" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', border: `1px solid rgba(196,146,85,0.4)`,
              borderRadius: 4, background: 'transparent', color: BN.accent, fontSize: 11, cursor: 'pointer',
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {isInstalling ? 'Installing...' : 'Install'}
          </button>
        )}

        <NotificationCenter />

        <PrivacyToggle />

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button type="button" onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu" aria-expanded={menuOpen}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%',
              background: BN.accent, color: BN.bg,
              display: 'grid', placeItems: 'center',
              fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 13, fontStyle: 'italic',
            }}>
              {initial}
            </span>
          </button>

          {menuOpen && (
            <div role="menu" style={{
              position: 'absolute', right: 0, marginTop: 8, width: 220,
              borderRadius: 4, border: `1px solid ${BN.hair2}`,
              background: BN.bg2, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              overflow: 'hidden', zIndex: 50,
            }}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${BN.hair}` }}>
                <p style={{ fontSize: 13, color: BN.ink, margin: 0 }}>{userDoc?.display_name ?? '-'}</p>
                <p style={{ fontSize: 11, color: BN.ink3, fontFamily: "'JetBrains Mono', monospace", margin: '2px 0 0' }}>{userDoc?.email}</p>
                <p style={{ fontSize: 11, color: BN.ink4, textTransform: 'uppercase', letterSpacing: '0.18em', fontWeight: 500, margin: '4px 0 0' }}>
                  {userDoc?.role === 'owner' ? 'Owner' : 'Guest'}
                </p>
              </div>
              {userDoc?.role === 'owner' && (
                <button type="button" role="menuitem"
                  onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                  style={menuBtn}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = BN.bg3; (e.currentTarget as HTMLButtonElement).style.color = BN.ink; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = BN.ink3; }}>
                  Settings
                </button>
              )}
              <button type="button" role="menuitem"
                onClick={() => { setMenuOpen(false); void signOut(); }}
                style={menuBtn}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = BN.bg3; (e.currentTarget as HTMLButtonElement).style.color = BN.ink; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = BN.ink3; }}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
